// ─── Beam Manager ─────────────────────────────────────────────────────────────
// Manages beam placement, types, snap logic, and position management.
// Beam types: accessory (fan beam), structural (header beam), heater beam, reinforcement

export const BEAM_TYPES = {
  accessory: {
    label: 'Accessory Beam (Fan Beam)',
    shortLabel: 'Fan Beam',
    description: 'Non-structural, for mounting fans/accessories',
    color: 0x5a7a9a,
    widthIn: 4,    // 4" x 4" fan beam
    heightIn: 4,
  },
  structural: {
    label: 'Structural Beam (Header Beam)',
    shortLabel: 'Structural Beam',
    description: 'Primary load-bearing, spans large openings',
    color: 0x8a5a3a,
    widthIn: 5.75,
    heightIn: 5.75,
  },
  heater: {
    label: 'Integrated Heater Beam',
    shortLabel: 'Heater Beam',
    description: 'Holds one Infratech recessed heater with vents',
    color: 0x9a4a3a,
    widthIn: 5.75,
    heightIn: 5.75,
  },
  reinforcement: {
    label: 'Reinforcement (Pass-Through Gutter)',
    shortLabel: 'Reinforcement',
    description: 'Strengthens bay/roof connections for multi-bay',
    color: 0x6a6a5a,
    widthIn: 5.75,
    heightIn: 5.75,
  },
};

// Snap points for beam placement
const SNAP_THRESHOLD_IN = 6; // snap within 6 inches

export class BeamManager {
  constructor() {
    // Each beam: { id, type, positionIn, axis }
    // positionIn = distance from center along the perpendicular axis (inches)
    // axis = 'width' (beam runs along width/X) or 'length' (beam runs along length/Z)
    this.beams = [];
    this.nextId = 1;
  }

  addBeam(type, axis = 'width', positionIn = 0) {
    if (!BEAM_TYPES[type]) return null;
    const beam = {
      id: this.nextId++,
      type,
      axis,
      positionIn, // offset from center in inches along perpendicular axis
    };
    this.beams.push(beam);
    return beam;
  }

  removeBeam(id) {
    this.beams = this.beams.filter(b => b.id !== id);
  }

  moveBeam(id, newPositionIn) {
    const beam = this.beams.find(b => b.id === id);
    if (beam) beam.positionIn = newPositionIn;
  }

  getBeams() {
    return [...this.beams];
  }

  getBeamsByType(type) {
    return this.beams.filter(b => b.type === type);
  }

  // ─── Snap logic ──────────────────────────────────────────────────
  // Returns snap points for a given pergola dimension
  getSnapPoints(widthInches, lengthInches, axis) {
    // axis = which axis the beam runs along
    // perpendicular dimension is what we snap to
    const perpDim = axis === 'width' ? lengthInches : widthInches;
    const halfDim = perpDim / 2;

    const snaps = [
      { positionIn: 0, label: 'Center' },
      { positionIn: -halfDim * 0.25, label: '25%' },
      { positionIn: halfDim * 0.25, label: '75%' },
      { positionIn: -halfDim * 0.5, label: 'Quarter from edge' },
      { positionIn: halfDim * 0.5, label: 'Quarter from edge' },
    ];

    return snaps;
  }

  // Snap a position to nearest snap point if within threshold
  snapPosition(positionIn, widthInches, lengthInches, axis) {
    const snaps = this.getSnapPoints(widthInches, lengthInches, axis);
    let closest = null;
    let closestDist = Infinity;

    for (const snap of snaps) {
      const dist = Math.abs(positionIn - snap.positionIn);
      if (dist < closestDist) {
        closestDist = dist;
        closest = snap;
      }
    }

    if (closestDist <= SNAP_THRESHOLD_IN) {
      return { snapped: true, positionIn: closest.positionIn, label: closest.label };
    }
    return { snapped: false, positionIn, label: null };
  }

  // ─── Cantilever validation ───────────────────────────────────────
  // Max cantilever is typically 1/3 of the bay dimension on that axis
  getMaxCantilever(bayDimensionIn) {
    return Math.floor(bayDimensionIn / 3);
  }

  validatePosition(positionIn, widthInches, lengthInches, axis) {
    const perpDim = axis === 'width' ? lengthInches : widthInches;
    const halfDim = perpDim / 2;
    const maxOffset = halfDim; // can't go beyond the edge

    if (Math.abs(positionIn) > maxOffset) {
      return {
        valid: false,
        reason: `Position exceeds pergola boundary (max ${maxOffset.toFixed(0)}" from center)`,
        clamped: Math.sign(positionIn) * maxOffset,
      };
    }

    return { valid: true, reason: null, clamped: positionIn };
  }

  // Get count of beams by type for pricing
  getBeamCounts() {
    const counts = {};
    for (const beam of this.beams) {
      counts[beam.type] = (counts[beam.type] || 0) + 1;
    }
    return counts;
  }
}
